import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { DEFAULT_PRIMARY_FONT_FAMILY } from 'theme/fontDefaults';

// ─── Outer wrapper ───────────────────────────────────────────────────────────
export const StScheduleWrapper = styled(Box)(({ theme }) => ({
  padding: '0.5rem 2.5rem 3rem',
  width: '100%',
  boxSizing: 'border-box',
  overflowX: 'hidden',
  [theme.breakpoints.up('lg')]: {
    width: '80%',
  },
  [theme.breakpoints.down('md')]: {
    padding: '1rem 2.5rem',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '0',
  },
}));

// ─── Card / form container ───────────────────────────────────────────────────
export const StFormCard = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  overflowX: 'visible',
  [theme.breakpoints.down('md')]: {
    border: 'none',
    boxShadow: 'none',
    padding: '1rem',
  },
  [theme.breakpoints.down('sm')]: {
    borderRadius: '0',
    padding: '1rem 1rem 1.5rem',
    overflowX: 'hidden',
  },
}));

// ─── Section heading ─────────────────────────────────────────────────────────
export const StHeading = styled(Typography)(({ theme }) => ({
  fontFamily: `${theme?.palette?.primaryFontFamilyType || DEFAULT_PRIMARY_FONT_FAMILY}, sans-serif`,
  fontSize: '22px',
  fontWeight: 700,
  color: theme.palette.themeColor?.main || '#005587',
  marginBottom: '0.45rem',
  [theme.breakpoints.down('sm')]: {
    fontSize: '20px',
    marginBottom: '0.25rem',
  },
}));

export const StSubHeading = styled(Typography)(({ theme }) => ({
  fontFamily: `${theme?.palette?.primaryFontFamilyType || DEFAULT_PRIMARY_FONT_FAMILY}, sans-serif`,
  fontSize: '14px',
  fontWeight: 400,
  color: theme.palette.neutral8?.main || '#444',
  marginBottom: '1.5rem',
  [theme.breakpoints.down('sm')]: {
    fontSize: '13px',
    marginBottom: '0.85rem',
  },
}));

// ─── Selected site card ───────────────────────────────────────────────────────
export const StSiteCard = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.neutral4?.main || '#d9d9d9'}`,
  borderRadius: '8px',
  padding: '0.75rem 1rem',
  marginBottom: '1.25rem',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '0.5rem',
  [theme.breakpoints.down('sm')]: {
    padding: '0.6rem 0.75rem',
    borderRadius: '6px',
  },
}));

export const StSiteName = styled(Typography)(({ theme }) => ({
  fontFamily: `${theme?.palette?.primaryFontFamilyType || DEFAULT_PRIMARY_FONT_FAMILY}, sans-serif`,
  fontSize: '15px',
  fontWeight: 700,
  color: theme.palette.black?.main || '#000',
}));

export const StSiteDetail = styled(Typography)(({ theme }) => ({
  fontFamily: `${theme?.palette?.primaryFontFamilyType || DEFAULT_PRIMARY_FONT_FAMILY}, sans-serif`,
  fontSize: '14px',
  fontWeight: 400,
  color: theme.palette.neutral8?.main || '#444',
  lineHeight: '1.5',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
}));

export const StSiteMetaRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '1rem',
  marginTop: '0.8rem',
  flexWrap: 'wrap',
}));

export const StDateLabel = styled(Typography)(({ theme }) => ({
  fontFamily: `${theme?.palette?.primaryFontFamilyType || DEFAULT_PRIMARY_FONT_FAMILY}, sans-serif`,
  fontSize: '16px',
  fontWeight: 600,
  color: theme.palette.black?.main || '#000',
  marginBottom: '0.25rem',
}));

export const StMonthLabel = styled(Typography)(({ theme }) => ({
  fontFamily: `${theme?.palette?.primaryFontFamilyType || DEFAULT_PRIMARY_FONT_FAMILY}, sans-serif`,
  fontSize: '15px',
  fontWeight: 600,
  color: theme.palette.neutral8?.main || '#444',
  marginBottom: '1rem',
}));

export const StDateCarouselWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: '0.35rem',
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  paddingBottom: '4px',
  [theme.breakpoints.down('sm')]: {
    gap: '0.25rem',
    paddingBottom: '0',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    scrollSnapType: 'x mandatory',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
    maxWidth: 'calc(100vw - 2rem)',
  },
}));

export const StNavArrow = styled(Box)<{ side?: 'left' | 'right' }>(({ theme, side }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  ...(side === 'left' ? { right: '100%', marginRight: '0.5rem' } : {}),
  ...(side === 'right' ? { left: '100%', marginLeft: '0.5rem' } : {}),
  zIndex: 1,
  flexShrink: 0,
  width: 28,
  height: 28,
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: theme.palette.neutral8?.main || '#444',
  fontSize: '14px',
  userSelect: 'none',
  borderRadius: '50%',
  border: '1px solid transparent',
  '&:hover': {
    background: '#0557D514',
    borderColor: '#015FF1',
  },
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

export const StDayCell = styled(Box)<{ selected?: boolean; disabled?: boolean }>(
  ({ theme, selected, disabled }) => ({
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    padding: '0.4rem 0.55rem',
    minWidth: 0,
    boxSizing: 'border-box',
    scrollSnapAlign: 'start',
    cursor: disabled ? 'not-allowed' : 'pointer',
    background: selected
      ? theme.palette.primary?.main || '#005587'
      : theme.palette.neutral0?.main || '#fff',
    border: selected
      ? `2px solid ${theme.palette.primary?.main || '#005587'}`
      : `1px solid ${theme.palette.neutral4?.main || '#808080'}`,
    opacity: disabled ? 0.4 : 1,
    '&:hover': {
      background: disabled
        ? undefined
        : selected
        ? undefined
        : theme.palette.neutral2?.main || '#0557D514',
    },
    '& .day-name': {
      fontSize: '11px',
      fontWeight: 600,
      color: selected ? '#fff' : theme.palette.neutral7?.main || '#595959',
      fontFamily: `${
        theme?.palette?.primaryFontFamilyType || DEFAULT_PRIMARY_FONT_FAMILY
      }, sans-serif`,
    },
    '& .day-number': {
      fontSize: '16px',
      fontWeight: 700,
      color: selected ? '#fff' : theme.palette.black?.main || '#000',
      fontFamily: `${
        theme?.palette?.primaryFontFamilyType || DEFAULT_PRIMARY_FONT_FAMILY
      }, sans-serif`,
      lineHeight: '1.2',
    },
    '& .day-slots': {
      fontSize: '11px',
      fontWeight: 600,
      fontFamily: `${
        theme?.palette?.primaryFontFamilyType || DEFAULT_PRIMARY_FONT_FAMILY
      }, sans-serif`,
      marginTop: '3px',
      whiteSpace: 'nowrap',
      textTransform: 'uppercase',
      ...(selected
        ? {
            color: '#013D98',
            background: '#E6F0FF',
            padding: '0 6px',
            borderRadius: '20px',
          }
        : {
            color: theme.palette.neutral7?.main || '#595959',
            background: 'transparent',
            padding: '0',
          }),
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: '80px',
      padding: '0.35rem 0.4rem',
      '& .day-name': { fontSize: '10px' },
      '& .day-number': { fontSize: '14px' },
      '& .day-slots': { fontSize: '9px', fontWeight: 400, padding: '0 10px' },
    },
  })
);

// ─── Time slots list ──────────────────────────────────────────────────────────
export const StTimeSlotsContainer = styled(Box)(({ theme }) => ({
  maxHeight: '320px',
  overflowY: 'auto',
  overflowX: 'hidden',
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  paddingRight: '4px',
  [theme.breakpoints.down('sm')]: {
    maxHeight: '260px',
  },
}));

export const StTimeSlotItem = styled(Box)<{ selected?: boolean; disabled?: boolean }>(
  ({ theme, selected, disabled }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    // Allow the slots-left badge to wrap beneath the time label when the grid
    // cell becomes too narrow (tablet / narrow desktop) instead of overflowing.
    flexWrap: 'wrap',
    gap: '0.4rem 0.5rem',
    border: `1px solid ${
      selected
        ? theme.palette.primary?.main || '#005587'
        : theme.palette.neutral4?.main || '#808080'
    }`,
    borderRadius: '6px',
    padding: '0.85rem 1rem',
    width: '100%',
    maxWidth: '100%',
    height: '100%',
    boxSizing: 'border-box',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    pointerEvents: disabled ? 'none' : 'auto',
    background: selected ? `#0557D514` : theme.palette.neutral0?.main || '#fff',
    transition: 'border-color 0.15s, background 0.15s',
    '&:hover': {
      background: disabled ? undefined : 'rgb(0,85,135,0.04)',
      borderColor: disabled ? undefined : theme.palette.primary?.main || '#005587',
    },
    [theme.breakpoints.down('sm')]: {
      padding: '0.7rem 0.75rem',
    },
  })
);

export const StTimeLabel = styled(Typography)(({ theme }) => ({
  fontFamily: `${theme?.palette?.primaryFontFamilyType || DEFAULT_PRIMARY_FONT_FAMILY}, sans-serif`,
  fontSize: '14px',
  fontWeight: 500,
  color: theme.palette.neutral8?.main || '#444',
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('sm')]: {
    fontSize: '13px',
  },
}));

export const StSlotsLeftBadge = styled(Typography)(({ theme }) => ({
  fontFamily: `${theme?.palette?.primaryFontFamilyType || DEFAULT_PRIMARY_FONT_FAMILY}, sans-serif`,
  fontSize: '12px',
  fontWeight: 600,
  background: '#E6E6E6',
  // color: theme.palette.primary?.main || '#005587',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  flexShrink: 0,
  whiteSpace: 'nowrap',
  padding: '2px 6px',
  borderRadius: '12px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '11px',
    background: theme.palette.neutral2?.main || '#F2F2F2',
    color: theme.palette.neutral7?.main || '#595959',
    padding: '2px 8px',
    borderRadius: '4px',
  },
}));

// ─── Timer row ────────────────────────────────────────────────────────────────
export const StTimerRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
  marginBottom: '1.25rem',
  marginTop: '0.5rem',
  [theme.breakpoints.down('sm')]: {
    marginBottom: '1rem',
  },
}));

export const StTimerText = styled(Typography)<{ lowTime?: boolean }>(({ theme, lowTime }) => ({
  fontFamily: `${theme?.palette?.primaryFontFamilyType || DEFAULT_PRIMARY_FONT_FAMILY}, sans-serif`,
  fontSize: '13px',
  color: lowTime ? '#d32f2f' : theme.palette.neutral8?.main || '#444',
  transition: 'color 0.3s',
  fontWeight: lowTime ? 600 : 400,
  '& strong': {
    color: lowTime ? '#d32f2f' : 'inherit',
  },
}));

// ─── Confirm button ───────────────────────────────────────────────────────────
export const StConfirmButton = styled('button')(({ theme }) => ({
  background: theme.palette.primary?.main || '#005587',
  color: '#fff',
  border: 'none',
  borderRadius: '24px',
  padding: '0.65rem 1.75rem',
  fontSize: '15px',
  fontWeight: 600,
  fontFamily: `${theme?.palette?.primaryFontFamilyType || DEFAULT_PRIMARY_FONT_FAMILY}, sans-serif`,
  cursor: 'pointer',
  marginBottom: '0',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    opacity: 0.9,
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '0.8rem 1rem',
    fontSize: '15px',
    borderRadius: '28px',
  },
}));

// ─── Error / info banner (S4, S5) ─────────────────────────────────────────────
export const StAlertBanner = styled(Box)<{ severity?: 'error' | 'info' }>(
  ({ theme, severity }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.5rem',
    padding: '0.75rem 1rem',
    borderRadius: '6px',
    marginBottom: '1rem',
    scrollMarginTop: '7.25rem',
    [theme.breakpoints.down('sm')]: {
      scrollMarginTop: '5rem',
    },
    background: severity === 'error' ? 'rgba(211,47,47,0.08)' : 'rgba(2,136,209,0.08)',
    border: `1px solid ${severity === 'error' ? '#d32f2f' : '#0288d1'}`,
    fontSize: '14px',
    fontFamily: `${
      theme?.palette?.primaryFontFamilyType || DEFAULT_PRIMARY_FONT_FAMILY
    }, sans-serif`,
    color: severity === 'error' ? '#b71c1c' : '#01579b',
    fontWeight: 500,
  })
);
